import React, { useContext, useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Calendar } from 'primereact/calendar';
import { dayjsFromLocalDateString, shortDateFormat, stringYMDFromDate } from '@utils/dateWithTz.tsx';
import {
  AccessTypeEnum,
  AttachmentUploadedOutDTO,
  EntityTypeEnum,
  LabelPreviewWithoutTypeOutDTO,
  LovDocumentTypeOutDTO,
} from '@api/ringil-api.v2';
import useForm from '@hooks/useForm/useForm';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { InputDocumentType } from '@components/obsolete/Form/InputDocumentType/InputDocumentType.tsx';
import { useTranslation } from 'react-i18next';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import styled from 'styled-components';
import LinkSvg from '@icons/LinkSvg';
import { HorizontalLine } from '@components/ringil3/styles.tsx';
import SvgTabularDeleteIcon from '@components/framework/icons/TabularDeleteIcon';
import AddonsSvg from '@app/icons/AddonsSvg';
import CustomTooltip from '../../Tooltip/CustomTooltip';
import { Field } from '@components/framework/formfield/Field.tsx';
import { findValidationMessage } from '@utils/validation.tsx';
import UploadPanel from '@components/framework/uploadpanel/UploadPanel.tsx';
import LabelsTag from '@components/framework/tags/LabelsTag.tsx';
import RingilInputSwitch from '@components/framework/input/RingilInputSwitch/RingilInputSwitch.tsx';
import InputSelect from '@components/ringil3/Input/InputSelect/InputSelect.tsx';
import RingilTextArea from '@components/framework/input/RingilTextArea.tsx';
import { ApiContext } from '@api/api';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';


const NOTE_MAX_LENGTH = 255;

export interface AttachmentDocument extends Omit<AttachmentUploadedOutDTO, 'documentType' | 'documentTypeName'> {
  documentType: LovDocumentTypeOutDTO;
  labelIds?: string[];
  labels?: LabelPreviewWithoutTypeOutDTO[];
}

export type UploadedAttachment = AttachmentUploadedOutDTO & { labels?: LabelPreviewWithoutTypeOutDTO[] };

interface ComponentProps {
  visible: boolean;
  onHide: () => void;
  pendingFiles?: File[];
  // With an entity the sidebar attaches the documents itself; without one it hands them back via onCompleted.
  entityType?: EntityTypeEnum;
  entityIds?: string[];
  onAttached?: (documentCount: number) => void;
  onCompleted?: (documents: UploadedAttachment[]) => void;
}

const FileCard = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  font-family: 'DM Sans', sans-serif;
`;
const FileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: 'DM Sans', sans-serif;
`;
const FileName = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #343333;
  font-size: 0.9rem;
  font-weight: 400;
  font-family: 'DM Sans', sans-serif;
  padding: 0 12px;
`;
const DeleteButton = styled.button`
  margin-right: 24px;
  background: none;
  border: none;
  color: #fe5000;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background 0.15s;
  font-family: 'DM Sans', sans-serif;
  &:hover {
    background: #fff5f0;
  }
`;
const FieldLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-family: 'DM Sans', sans-serif;
`;
const FieldLabel = styled.label`
  color: #616061;
  font-size: 0.9rem;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
`;

const StyledCalendar = styled(Calendar)`
  width: 100%;
  font-family: 'DM Sans', sans-serif;
  .p-inputtext {
    width: 100%;
    border: 1px solid #616061;
    font-size: 1rem;
    color: #616061;
    background: #fff;
    padding: 10px 12px;
    font-family: 'DM Sans', sans-serif;
    &:focus {
      outline: none;
      border-color: #fe5000;
    }
  }
  .p-calendar input.p-inputtext.p-component {
    border: 1px solid #616061 !important;
  }
  .p-calendar-w-btn .p-datepicker-trigger {
    background: transparent !important;
    border: none !important;
    color: #616061 !important;
    box-shadow: none !important;
  }
  .p-calendar-w-btn .p-datepicker-trigger:hover,
  .p-calendar-w-btn .p-datepicker-trigger:focus,
  .p-calendar-w-btn .p-datepicker-trigger:active {
    background: transparent !important;
    border: none !important;
    color: #616061 !important;
    box-shadow: none !important;
  }
`;
const StyledInputDocumentType = styled(InputDocumentType)`
  font-family: 'DM Sans', sans-serif;
  .p-dropdown.p-inputwrapper.p-component {
    border: 1px solid #616061 !important;
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    font-size: 1rem;
    color: #616061;
    background: #fff;
    padding: 10px 12px;
    font-family: 'DM Sans', sans-serif;
    &:focus {
      outline: none;
      border-color: #fe5000;
    }
  }
`;

interface AttachmentFileCardProps {
  docIndex: number;
  document: AttachmentDocument;
  fileNames?: string[];
  tr: (key: string, defaultText?: string, data?: any, count?: number) => string;
  language: string;
  validationErrors: any;
  attachmentTypeModel: { code: AccessTypeEnum; label: string }[];
  formFieldName: (index: number, name: keyof AttachmentDocument) => string;
  handleDelete: (docIndex: number) => void;
  handleDocumentTypeChange: (docIndex: number, documentType: LovDocumentTypeOutDTO) => void;
  handleAccessTypeChange: (docIndex: number, accessType: AccessTypeEnum) => void;
  handleDateChange: (docIndex: number, date: Date) => void;
  handleLabelsTagChange: (docIndex: number, labels: LabelPreviewWithoutTypeOutDTO[]) => void;
  handleNoteChange: (docIndex: number, note: string) => void;
}

const AttachmentFileCard: React.FC<AttachmentFileCardProps> = ({
                                                                 docIndex,
                                                                 document,
                                                                 fileNames,
                                                                 tr,
                                                                 language,
                                                                 validationErrors,
                                                                 attachmentTypeModel,
                                                                 formFieldName,
                                                                 handleDelete,
                                                                 handleDocumentTypeChange,
                                                                 handleAccessTypeChange,
                                                                 handleDateChange,
                                                                 handleLabelsTagChange,
                                                                 handleNoteChange,
                                                               }) => {
  return (
    <FileCard>
      <FileHeader>
        <FileName>
          <LinkSvg width={16} height={16} />
          {fileNames && fileNames.length > 0 ? fileNames.join(' | ') : document.fileName}
        </FileName>
        <DeleteButton onClick={() => handleDelete(docIndex)} title={tr('UploadAttachmentDialog.delete', 'Delete')}>
          <SvgTabularDeleteIcon fill={'#fe5000'} />
        </DeleteButton>
      </FileHeader>

      <Field
        label={tr('UploadAttachmentDialog.documentType', 'Document type')}
        required={true}
        errorMessage={
          findValidationMessage(validationErrors, `[${docIndex}].documentType.documentType`, tr) ||
          findValidationMessage(validationErrors, `[${docIndex}].documentType`, tr)
        }
      >
        <StyledInputDocumentType
          style={{ width: '100%', fontFamily: '\'DM Sans\', sans-serif' }}
          documentType={document.documentType?.documentType}
          filter
          onChange={v => handleDocumentTypeChange(docIndex, v)}
        />
      </Field>

      <div>
        <FieldLabelRow>
          <FieldLabel>{tr('UploadAttachmentDialog.accessType', 'Visibility')}</FieldLabel>
          <span
            id={`accessType-span-${docIndex}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 25,
              height: 25,
              borderRadius: '50%',
              background: '#e7ebee',
              color: '#616061',
              fontWeight: 500,
              fontSize: '1rem',
              marginLeft: 6,
              padding: 10,
            }}
          >
            ?
          </span>
          <CustomTooltip
            content={tr('UploadAttachmentDialog.sharedDocumentInfo', 'Sdílený dokument je viditelný také vašim partnerům')}
            targetSelector={`#accessType-span-${docIndex}`}
          />
        </FieldLabelRow>
        <InputSelect
          model={attachmentTypeModel}
          selectedItemCode={document.accessType}
          onSelect={v => {
            handleAccessTypeChange(docIndex, (v as AccessTypeEnum));
          }}
        />
      </div>

      <Field label={tr('UploadAttachmentDialog.date', 'Date')}>
        <StyledCalendar
          id={`date-${docIndex}`}
          showIcon
          icon={<AddonsSvg width={20} height={20} />}
          value={document.userDateLocalDate ? dayjsFromLocalDateString(document.userDateLocalDate).toDate() : null}
          onChange={e => handleDateChange(docIndex, e.value as Date)}
          dateFormat={shortDateFormat}
          selectionMode={'single'}
          locale={language}
          hideOnDateTimeSelect={true}
        />
      </Field>

      <div>
        <LabelsTag
          value={document.labels}
          objectType={EntityTypeEnum.ATTACHMENTS}
          resolveLabelsForType={EntityTypeEnum.ATTACHMENTS}
          disabled={false}
          wrapperWidth="500px"
          onChange={v => handleLabelsTagChange(docIndex, v)}
        />
      </div>

      <Field
        label={tr('UploadAttachmentDialog.note', 'Note')}
        errorMessage={findValidationMessage(validationErrors, formFieldName(docIndex, 'userNote'), tr)}
      >
        <RingilTextArea
          value={document.userNote ?? ''}
          rows={4}
          maxLength={NOTE_MAX_LENGTH}
          onChange={v => handleNoteChange(docIndex, v)}
        />
      </Field>
      <HorizontalLine variant="tertiary" />
    </FileCard>
  );
};

const UploadAttachmentDialog: React.FC<ComponentProps> = ({ onHide, visible, pendingFiles, entityType, entityIds, onAttached, onCompleted }) => {
  const { tr } = useTranslationLgs();
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const { genericUploadV2 } = useContext(ApiContext);
  const { i18n } = useTranslation();
  const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();
  const [loading, setLoading] = useState(false);

  const [isMultiUpload, setIsMultiUpload] = useState<boolean>(false);

  const form = useForm<AttachmentDocument[]>(
    yup.array().of(
      yup.object().shape({
        documentType: yup
          .object()
          .shape({ documentType: yup.string().required(tr('validation.required', 'Value must be entered')) })
          .required(tr('validation.required', 'Value must be entered')),
        accessType: yup.string().required().oneOf(Object.keys(AccessTypeEnum)),
        userNote: yup
          .string()
          .nullable()
          .max(NOTE_MAX_LENGTH, tr('validation.maxLength', 'Value must be at most {{max}} characters', { max: NOTE_MAX_LENGTH })),
      }),
    ),
    [],
    data => save(data),
    false,
    false,
  );

  const isAttachMode = entityType != null && entityIds?.length > 0;

  const save = (data: AttachmentDocument[]) => {
    if (!isAttachMode) {
      onCompleted?.(
        data.map(t => {
          return {
            ...t,
            documentType: t.documentType.documentType,
            documentTypeName: t.documentType.name,
          } as UploadedAttachment;
        }),
      );
      return;
    }
    setLoading(true);
    genericUploadV2(
      {
        entityType,
        entityIds,
        attachments: data.map(t => ({
          attachmentId: t.attachmentId,
          documentType: t.documentType?.documentType,
          accessType: t.accessType,
          userDateLocalDate: t.userDateLocalDate,
          userNote: t.userNote,
          labelIds: t.labels?.map(l => l.labelId) ?? [],
        })),
      },
      () => {
        setLoading(false);
        onAttached?.(data.length);
      },
      null,
      {
        onValidationFailed: errors => {
          setBeValidationErrors(errors);
          setLoading(false);
        },
        onError: (_error, showStandardError) => {
          showStandardError();
          setLoading(false);
        },
      },
    );
  };

  useEffect(() => {
    if (visible) {
      form.overwriteValues([]);
      setBeValidationErrors([]);
      setLoading(false);
    }
  }, [visible]);

  const formFieldName = (index: number, name: keyof AttachmentDocument) => `[${index}].${name}`;

  const currentFormState = useRef({ values: form.values, isMultiUpload });
  currentFormState.current = { values: form.values, isMultiUpload };

  const addDocuments = (attachments: AttachmentUploadedOutDTO[]) => {
    const { values, isMultiUpload: multi } = currentFormState.current;
    const shared = multi && values.length > 0 ? values[0] : null;
    const newDocuments: AttachmentDocument[] = attachments.map(attachment => ({
      attachmentId: attachment.attachmentId,
      fileName: attachment.fileName,
      companyName: attachment.companyName,
      accessType: AccessTypeEnum.ALL,
      contentType: attachment.contentType,
      companyId: attachment.companyId,
      size: attachment.size,
      uploadedAt: attachment.uploadedAt,
      userId: attachment.userId,
      userName: attachment.userName,
      accessTypeName: attachment.accessTypeName,
      canBeDeleted: attachment.canBeDeleted,
      documentType: {
        documentType: attachment.documentType,
        name: attachment.documentTypeName,
      },
      userDateLocalDate: attachment.userDateLocalDate,
      userNote: attachment.userNote,
      processedByAi: attachment.processedByAi,
      isAiProcessingEnabledForOwnerCompany: attachment.isAiProcessingEnabledForOwnerCompany,
      ...(shared && {
        documentType: shared.documentType,
        accessType: shared.accessType,
        userDateLocalDate: shared.userDateLocalDate,
        userNote: shared.userNote,
        labels: shared.labels,
      }),
    }));
    const next = [...values, ...newDocuments];
    currentFormState.current = { ...currentFormState.current, values: next };
    form.overwriteValues(next);
  };

  const handleDocumentTypeChange = (docIndex: number, documentType: LovDocumentTypeOutDTO) => {
    if (!isMultiUpload) {
      form.setFieldValue(formFieldName(docIndex, 'documentType'), documentType);
    } else {
      form.values.forEach((_, index) => {
        form.setFieldValue(formFieldName(index, 'documentType'), documentType);
      });
    }
  };

  const handleContinue = () => {
    form.validateAndSend();
  };

  const handleDelete = (docIndex: number) => {
    if (!isMultiUpload) {
      form.overwriteValues(form.values.filter((_, index) => index !== docIndex));
    } else {
      form.overwriteValues([]);
    }
  };

  const handleAccessTypeChange = (docIndex: number, accessType: AccessTypeEnum) => {
    if (!isMultiUpload) {
      form.setFieldValue(formFieldName(docIndex, 'accessType'), accessType);
    } else {
      form.values.forEach((_, index) => {
        form.setFieldValue(formFieldName(index, 'accessType'), accessType);
      });
    }
  };

  const handleDateChange = (docIndex: number, date: Date) => {
    if (!isMultiUpload) {
      form.setFieldValue(formFieldName(docIndex, 'userDateLocalDate'), stringYMDFromDate(date));
    } else {
      form.values.forEach((_, index) => {
        form.setFieldValue(formFieldName(index, 'userDateLocalDate'), stringYMDFromDate(date));
      });
    }
  };

  const handleNoteChange = (docIndex: number, note: string) => {
    if (!isMultiUpload) {
      form.setFieldValue(formFieldName(docIndex, 'userNote'), note);
    } else {
      form.values.forEach((_, index) => {
        form.setFieldValue(formFieldName(index, 'userNote'), note);
      });
    }
  };

  const handleLabelsTagChange = (docIndex: number, labels: LabelPreviewWithoutTypeOutDTO[]) => {
    if (isMultiUpload) {
      form.values.forEach((_, index) => {
        form.setFieldValue(formFieldName(index, 'labels'), labels);
      });
    } else {
      form.setFieldValue(formFieldName(docIndex, 'labels'), labels);
    }
  }

  const setAllDocumentsLikeFirst = () => {
    if (form.values.length < 2) return;
    const first = form.values[0];
    const updated = form.values.map(doc => ({
      ...doc,
      documentType: first.documentType,
      accessType: first.accessType,
      userDateLocalDate: first.userDateLocalDate,
      userNote: first.userNote,
      labels: first.labels,
    }));
    form.overwriteValues(updated);
  };

  const handleMultiUploadChange = (value: boolean) => {
    setIsMultiUpload(value);
    if (value) {
      setAllDocumentsLikeFirst();
    }
  }

  const attachmentTypeModel = [
    {
      code: AccessTypeEnum.INTERNAL,
      label: tr('UploadAttachmentDialog.internal', 'Internal'),
    },
    { code: AccessTypeEnum.ALL, label: tr('UploadAttachmentDialog.shared', 'Shared') },
  ];

  return (
    <RingilSidebar
      visible={visible}
      heading={tr('UploadAttachmentDialog.uploadDocuments', 'Upload documents')}
      onContinue={handleContinue}
      onBack={onHide}
      showSaveButton={true}
      continueLabel={tr('UploadAttachmentDialog.save', 'Save')}
      disableSaveButton={form.values.length === 0}
      loading={loading}
    >
      {/* Opts into the legacy PrimeReact overrides in styles/overrides/legacy-fields.scss. */}
      <div className={'r-legacy-calendar-fields contents'}>

      <BackendValidationComponent />

      <RingilInputSwitch
        value={isMultiUpload}
        onChange={v => handleMultiUploadChange(v)}
        label={tr('UploadAttachmentDialog.multiUpload', 'Use multi-upload')}
      />

      {!isMultiUpload && form.values.map((document, docIndex) => (
        <AttachmentFileCard
          key={document.attachmentId}
          docIndex={docIndex}
          document={document}
          tr={tr}
          language={i18n.language}
          validationErrors={form.validationErrors}
          attachmentTypeModel={attachmentTypeModel}
          formFieldName={formFieldName}
          handleDelete={handleDelete}
          handleDocumentTypeChange={handleDocumentTypeChange}
          handleAccessTypeChange={handleAccessTypeChange}
          handleDateChange={handleDateChange}
          handleLabelsTagChange={handleLabelsTagChange}
          handleNoteChange={handleNoteChange}
        />
      ))}

      {isMultiUpload && form.values && form.values.length > 0 && (
        <AttachmentFileCard
          docIndex={0}
          document={form.values[0]}
          fileNames={form.values.map(doc => doc.fileName)}
          tr={tr}
          language={i18n.language}
          validationErrors={form.validationErrors}
          attachmentTypeModel={attachmentTypeModel}
          formFieldName={formFieldName}
          handleDelete={handleDelete}
          handleDocumentTypeChange={handleDocumentTypeChange}
          handleAccessTypeChange={handleAccessTypeChange}
          handleDateChange={handleDateChange}
          handleLabelsTagChange={handleLabelsTagChange}
          handleNoteChange={handleNoteChange}
        />
      )}

      <div className="mt-4">
        <UploadPanel pendingFiles={pendingFiles} onSave={files => addDocuments(files as AttachmentUploadedOutDTO[])} />
      </div>

      </div>
    </RingilSidebar>
  );
};

export default UploadAttachmentDialog;

